import React, { useState, useEffect } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stepper,
  Step,
  StepLabel,
  Box,
  Typography,
  TextField,
  MenuItem,
  Stack,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  Chip,
} from '@mui/material';
import {
  Close as CloseIcon,
  QuestionMark as QuestionMarkIcon,
  Psychology as PsychologyIcon,
  Preview as PreviewIcon,
} from '@mui/icons-material';
import ImageUploader from '../common/EnhancedImageUpload';

const answerChoices = ['A', 'B', 'C', 'D', 'E', 'Others'];

const mistakeReasons = [
  'Misread the stimulus',
  'Misread the question stem',
  'Misunderstood the argument structure',
  'Fell for a trap answer',
  'Ran out of time/rushed',
  'Careless error',
  'Confused about question type',
  'Missed important keywords',
  'Failed to identify conclusion',
  'Other',
];

const steps = [
  {
    label: 'Question Info',
    icon: <QuestionMarkIcon />,
  },
  {
    label: 'Analysis',
    icon: <PsychologyIcon />,
  },
  {
    label: 'Review',
    icon: <PreviewIcon />,
  },
];

const AddEntryForm = ({ open, onClose, onSubmit, initialData, isEditing }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    ptNumber: '',
    sectionNumber: '',
    questionNumber: '',
    questionType: '',
    initialChoice: '',
    initialChoiceOther: '',
    correctAnswer: '',
    correctAnswerOther: '',
    mistakeReasons: [],
    explanation: '',
    notes: '',
    tags: [],
    image: null,
  });

  // Load initial data when editing
  useEffect(() => {
    if (isEditing && initialData) {
      setFormData({
        ptNumber: initialData.ptNumber || '',
        sectionNumber: initialData.sectionNumber || '',
        questionNumber: initialData.questionNumber || '',
        questionType: initialData.questionType || '',
        initialChoice: initialData.initialChoice || '',
        initialChoiceOther: initialData.initialChoiceOther || '',
        correctAnswer: initialData.correctAnswer || '',
        correctAnswerOther: initialData.correctAnswerOther || '',
        mistakeReasons: initialData.mistakeReasons || [],
        explanation: initialData.explanation || '',
        notes: initialData.notes || '',
        tags: initialData.tags || [],
        image: null, // Don't load the image directly, keep the URL in initialData
      });
    }
  }, [isEditing, initialData]);

  const handleNext = () => {
    if (activeStep === steps.length - 1) {
      handleSubmit();
    } else {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleSubmit = () => {
    onSubmit(formData);
    handleClose();
  };

  const handleClose = () => {
    setActiveStep(0);
    setFormData({
      ptNumber: '',
      sectionNumber: '',
      questionNumber: '',
      questionType: '',
      initialChoice: '',
      initialChoiceOther: '',
      correctAnswer: '',
      correctAnswerOther: '',
      mistakeReasons: [],
      explanation: '',
      notes: '',
      tags: [],
      image: null,
    });
    onClose();
  };

  const isStepValid = () => {
    switch (activeStep) {
      case 0:
        return formData.ptNumber && 
               formData.sectionNumber && 
               formData.questionNumber && 
               formData.questionType &&
               formData.initialChoice &&
               (formData.initialChoice !== 'Others' || formData.initialChoiceOther) &&
               formData.correctAnswer &&
               (formData.correctAnswer !== 'Others' || formData.correctAnswerOther);
      case 1:
        return formData.mistakeReasons.length > 0 && formData.explanation.length > 0;
      case 2:
        return true;
      default:
        return false;
    }
  };

  const modules = {
    toolbar: [
      [{ 'header': [1, 2, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'color': [] }, { 'background': [] }],
      ['clean']
    ],
  };

  const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet',
    'color', 'background'
  ];

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Stack spacing={3}>
            <Typography variant="subtitle1" color="text.secondary" gutterBottom>
              Enter the question details
            </Typography>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Question Image (Optional)
              </Typography>
              <ImageUploader
                onImageUpload={(imageBlob) => {
                  setFormData({ ...formData, image: imageBlob });
                }}
              />
            </Box>
            <TextField
              label="PrepTest Number"
              type="number"
              value={formData.ptNumber}
              onChange={(e) => setFormData({ ...formData, ptNumber: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Section Number"
              type="number"
              value={formData.sectionNumber}
              onChange={(e) => setFormData({ ...formData, sectionNumber: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Question Number"
              type="number"
              value={formData.questionNumber}
              onChange={(e) => setFormData({ ...formData, questionNumber: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Question Type"
              value={formData.questionType}
              onChange={(e) => setFormData({ ...formData, questionType: e.target.value })}
              fullWidth
              required
              placeholder="e.g. Assumption, Strengthen, Weaken, etc."
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <TextField
                  select
                  label="Your Initial Choice"
                  value={formData.initialChoice}
                  onChange={(e) => setFormData({ ...formData, initialChoice: e.target.value })}
                  fullWidth
                  required
                >
                  {answerChoices.map((choice) => (
                    <MenuItem key={choice} value={choice}>
                      {choice}
                    </MenuItem>
                  ))}
                </TextField>
                {formData.initialChoice === 'Others' && (
                  <TextField
                    label="Specify Other Choice"
                    value={formData.initialChoiceOther}
                    onChange={(e) => setFormData({ ...formData, initialChoiceOther: e.target.value })}
                    fullWidth
                    required
                    sx={{ mt: 2 }}
                  />
                )}
              </Box>
              <Box sx={{ flex: 1 }}>
                <TextField
                  select
                  label="Correct Answer"
                  value={formData.correctAnswer}
                  onChange={(e) => setFormData({ ...formData, correctAnswer: e.target.value })}
                  fullWidth
                  required
                >
                  {answerChoices.map((choice) => (
                    <MenuItem key={choice} value={choice}>
                      {choice}
                    </MenuItem>
                  ))}
                </TextField>
                {formData.correctAnswer === 'Others' && (
                  <TextField
                    label="Specify Other Answer"
                    value={formData.correctAnswerOther}
                    onChange={(e) => setFormData({ ...formData, correctAnswerOther: e.target.value })}
                    fullWidth
                    required
                    sx={{ mt: 2 }}
                  />
                )}
              </Box>
            </Box>
          </Stack>
        );
      case 1:
        return (
          <Stack spacing={3}>
            <Typography variant="subtitle1" color="text.secondary" gutterBottom>
              Analyze why you missed this question
            </Typography>
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Why did you miss this question? (Select all that apply)
              </Typography>
              <Box sx={{ 
                display: 'flex', 
                flexWrap: 'wrap', 
                gap: 1,
                mt: 1
              }}>
                {mistakeReasons.map((reason) => (
                  <Chip
                    key={reason}
                    label={reason}
                    onClick={() => {
                      setFormData(prev => {
                        const newReasons = prev.mistakeReasons.includes(reason)
                          ? prev.mistakeReasons.filter(r => r !== reason)
                          : [...prev.mistakeReasons, reason];
                        return { ...prev, mistakeReasons: newReasons };
                      });
                    }}
                    color={formData.mistakeReasons.includes(reason) ? "primary" : "default"}
                    variant={formData.mistakeReasons.includes(reason) ? "filled" : "outlined"}
                    sx={{ 
                      cursor: 'pointer',
                      '&:hover': {
                        bgcolor: (theme) => formData.mistakeReasons.includes(reason) 
                          ? theme.palette.primary.main 
                          : theme.palette.action.hover,
                      },
                    }}
                  />
                ))}
              </Box>
            </Box>
            <Box sx={{ 
              '& .ql-container': {
                height: '200px',
                fontSize: '1rem',
                lineHeight: '1.5',
              },
              '& .ql-editor': {
                minHeight: '200px',
              },
              '& .ql-toolbar': {
                borderTopLeftRadius: 1,
                borderTopRightRadius: 1,
              },
              '& .ql-container': {
                borderBottomLeftRadius: 1,
                borderBottomRightRadius: 1,
              },
            }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Detailed Explanation
              </Typography>
              <ReactQuill
                theme="snow"
                value={formData.explanation}
                onChange={(content) => setFormData({ ...formData, explanation: content })}
                modules={modules}
                formats={formats}
                placeholder="Explain in detail:
1. What was the core argument in the stimulus?
2. What was the question asking for?
3. Why was your chosen answer incorrect?
4. Why is the correct answer better?
5. What lesson can you learn from this mistake?"
              />
            </Box>
            <Box sx={{ mt: 4 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Additional Notes
              </Typography>
              <ReactQuill
                theme="snow"
                value={formData.notes}
                onChange={(content) => setFormData({ ...formData, notes: content })}
                modules={modules}
                formats={formats}
                placeholder="Add any additional insights, patterns, or reminders..."
                style={{ height: '150px' }}
              />
            </Box>
          </Stack>
        );
      case 2:
        return (
          <Stack spacing={3}>
            <Typography variant="subtitle1" color="text.secondary" gutterBottom>
              Review your entry
            </Typography>
            <Box sx={{ p: 2, bgcolor: 'background.neutral', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>Question Information</Typography>
              <Typography>PT {formData.ptNumber} • Section {formData.sectionNumber} • Question {formData.questionNumber}</Typography>
              <Typography color="primary" sx={{ mt: 1 }}>{formData.questionType}</Typography>
              <Box sx={{ mt: 1, display: 'flex', gap: 2 }}>
                <Typography color="error">
                  Your Answer: {formData.initialChoice === 'Others' ? formData.initialChoiceOther : formData.initialChoice}
                </Typography>
                <Typography color="success.main">
                  Correct Answer: {formData.correctAnswer === 'Others' ? formData.correctAnswerOther : formData.correctAnswer}
                </Typography>
              </Box>
              {formData.image && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>Question Image</Typography>
                  <img
                    src={URL.createObjectURL(formData.image)}
                    alt="Question"
                    style={{
                      maxWidth: '100%',
                      maxHeight: 200,
                      objectFit: 'contain',
                    }}
                  />
                </Box>
              )}
            </Box>
            <Box sx={{ p: 2, bgcolor: 'background.neutral', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>Mistake Reasons</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {formData.mistakeReasons.map((reason) => (
                  <Chip key={reason} label={reason} color="primary" variant="outlined" />
                ))}
              </Box>
            </Box>
            <Box sx={{ p: 2, bgcolor: 'background.neutral', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>Explanation</Typography>
              <div dangerouslySetInnerHTML={{ __html: formData.explanation }} />
            </Box>
            {formData.notes && (
              <Box sx={{ p: 2, bgcolor: 'background.neutral', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>Additional Notes</Typography>
                <div dangerouslySetInnerHTML={{ __html: formData.notes }} />
              </Box>
            )}
          </Stack>
        );
      default:
        return null;
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">
            {isEditing ? 'Edit Entry' : 'Add New Entry'}
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Stepper 
          activeStep={activeStep} 
          sx={{ py: 3 }}
          alternativeLabel
        >
          {steps.map((step, index) => (
            <Step key={step.label}>
              <StepLabel
                StepIconComponent={() => (
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      bgcolor: activeStep >= index ? 'primary.main' : 'grey.300',
                      color: activeStep >= index ? 'white' : 'grey.600',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {step.icon}
                  </Box>
                )}
              >
                {step.label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>

        <Box sx={{ mt: 3 }}>
          {renderStepContent()}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={handleClose}
          color="inherit"
        >
          Cancel
        </Button>
        <Box sx={{ flex: '1 1 auto' }} />
        {activeStep !== 0 && (
          <Button
            onClick={handleBack}
            sx={{ mr: 1 }}
          >
            Back
          </Button>
        )}
        <Button
          variant="contained"
          onClick={handleNext}
          disabled={!isStepValid()}
        >
          {activeStep === steps.length - 1 ? 'Add to Journal' : 'Next'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddEntryForm; 